// Intermediate, part 2: the plan gates (reward, costs, room to the target),
// the entry rules (don't chase, the 2-hour window), trade management (half
// risk against BTC, target 1 then breakeven, the time stop) and the engine's
// real 1h signals (would it fire here? where did it put the stop?).
import type { ChartSignal, ChartSignalResult } from "../../chart/chartSignals";
import { RESULT_META } from "../../chart/chartSignals";
import type { ChartLine, GameDef, Option, Rng } from "../types";
import { atr, chartOf, clockText, cutAt, emaLast, pctText, price, rText, roundTo, usd } from "../helpers";
import {
  AGAINST_BTC_RISK, CHASE_ATR, costR, ENTRY_WINDOW_HOURS, FEE_PCT_PER_SIDE, FUNDING_PCT_PER_8H, MAX_COST_R,
  MIN_ROOM_R, MIN_RR, roundTripCostPct, SLIPPAGE_PCT_PER_SIDE, STOP_BUFFER_ATR, STOP_LOOKBACK, TIME_STOP_HOURS,
  TP1_FALLBACK_R, TP2_R,
} from "../rules";

// ── Private helpers ─────────────────────────────────────────────────────────

type Side = "LONG" | "SHORT";

interface Coin { name: string; px: number; tick: number }

/** Made-up scenarios use these coins at roughly their usual prices, on a tick the price text shows in full. */
const COINS: Coin[] = [
  { name: "BTC", px: 64000, tick: 1 },
  { name: "ETH", px: 3200, tick: 0.1 },
  { name: "SOL", px: 145, tick: 0.01 },
  { name: "AVAX", px: 32, tick: 0.01 },
  { name: "LINK", px: 18, tick: 0.01 },
  { name: "XRP", px: 2.4, tick: 0.0001 },
  { name: "DOGE", px: 0.18, tick: 0.0001 },
];

const tickDecimals = (tick: number): number => Math.max(0, Math.round(-Math.log10(tick)));
const snap = (n: number, tick: number): number => roundTo(Math.round(n / tick) * tick, tickDecimals(tick));

const buyWord = (side: Side): string => (side === "LONG" ? "buy" : "sell");
const BuyWord = (side: Side): string => (side === "LONG" ? "Buy" : "Sell");
const BUYWORD = (side: Side): string => (side === "LONG" ? "BUY" : "SELL");

/** "0.42 ATR" / "−0.10 ATR" */
const atrText = (n: number): string => `${n < 0 ? "−" : ""}${Math.abs(n).toFixed(2)} ATR`;

/** "1h 35m" */
function durationText(minutes: number): string {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return h === 0 ? `${m}m` : m === 0 ? `${h}h` : `${h}h ${m}m`;
}

/** Decimals that keep about four significant digits of a coin amount. */
const sizeDecimals = (n: number): number => Math.min(8, Math.max(0, 3 - Math.floor(Math.log10(Math.abs(n)))));

interface Plan {
  side: Side;
  dir: 1 | -1;
  entry: number;
  stop: number;
  /** The risk per coin, in ticks: a multiple of 10, so 1-decimal R targets land on a tick. */
  riskTicks: number;
  risk: number;
}

/** A made-up plan: a side, an entry near the coin's price, and a stop `riskPct` away (a whole number of 10 ticks). */
function makePlan(coin: Coin, rng: Rng, riskPct: [number, number], side?: Side): Plan {
  const s: Side = side ?? (rng.chance(0.5) ? "LONG" : "SHORT");
  const dir = s === "LONG" ? 1 : -1;
  const entry = snap(coin.px * rng.float(0.9, 1.1), coin.tick);
  const pct = rng.float(riskPct[0], riskPct[1]);
  const riskTicks = Math.max(10, Math.round((entry * pct) / 100 / coin.tick / 10) * 10);
  const risk = snap(riskTicks * coin.tick, coin.tick);
  return { side: s, dir, entry, stop: snap(entry - dir * risk, coin.tick), riskTicks, risk };
}

/** The price `r` risks from the entry, in the trade's direction, on the coin's tick. */
const priceAtR = (p: Plan, r: number, coin: Coin): number => snap(p.entry + p.dir * r * p.risk, coin.tick);

/** The candles a signals game shows (and the reveal after), cut at a moment. */
const SIGNAL_HISTORY = 200;
const SIGNAL_AHEAD = 24;

const ARCHETYPE: Record<string, { name: string; LONG: string; SHORT: string }> = {
  trend_pullback: {
    name: "trend pullback",
    LONG: "in an up trend, price dipped back toward the EMAs and turned up",
    SHORT: "in a down trend, price bounced back toward the EMAs and turned down",
  },
  range_fade: {
    name: "range fade",
    LONG: "price tested the bottom of a sideways range and was pushed back up",
    SHORT: "price tested the top of a sideways range and was pushed back down",
  },
  breakout_retest: {
    name: "breakout retest",
    LONG: "price broke above a level, came back to test it, and held",
    SHORT: "price broke below a level, came back to test it, and stayed under it",
  },
};

function triggerText(trigger: string | null, side: Side): string | null {
  const long = side === "LONG";
  switch (trigger) {
    case "bullish_engulfing": return "a bullish engulfing candle";
    case "bearish_engulfing": return "a bearish engulfing candle";
    case "hammer": return "a hammer";
    case "shooting_star": return "a shooting star";
    case "level_reclaim": return long ? "a close above the last candle's high" : "a close below the last candle's low";
    case "ema_reclaim": return long ? "a close back above the EMA" : "a close back below the EMA";
    case "rejection_wick": return "a rejection wick (a long wick pushed back from the level)";
    case null: case undefined: case "": return null;
    default: return trigger.replace(/_/g, " ");
  }
}

/** "a trend pullback (in an up trend, …), triggered by a hammer" */
function setupText(s: ChartSignal): string {
  const a = s.archetype ? ARCHETYPE[s.archetype] : undefined;
  const what = a ? `a ${a.name} (${a[s.action]})` : s.archetype ? `a ${s.archetype.replace(/_/g, " ")} setup` : "its setup";
  const t = triggerText(s.trigger, s.action);
  return t ? `${what}, triggered by ${t}` : what;
}

function thesisText(s: ChartSignal): string {
  const t = (s.thesis ?? "").trim();
  if (!t) return "";
  const short = t.length > 180 ? `${t.slice(0, 177).trimEnd()}…` : t;
  return ` The engine's note: “${short}”${/[.!?…]$/.test(short) ? "" : "."}`;
}

/** What a signal did next, for `outcome` only (never graded). */
function signalOutcome(s: ChartSignal): string {
  const meta = RESULT_META[s.result as ChartSignalResult];
  if (!meta) return "";
  const r = s.r !== null && Number.isFinite(s.r) ? ` (${rText(s.r)})` : "";
  return `This signal: ${meta.label}${r}. ${meta.plain}`;
}

/** The engine's signals in a signals game's data (a trade the user logged isn't one). */
const engineSignals = (signals: ChartSignal[] | undefined): ChartSignal[] => (signals ?? []).filter(s => s.source !== "mine");

/** Lines for a signal's plan, drawn after answering. */
function planLines(s: ChartSignal): ChartLine[] {
  return [
    { price: s.entry, label: "Entry", tone: "brand" },
    { price: s.stopLoss, label: "Stop", tone: "loss", dashed: true },
    { price: s.tp1, label: "Target 1", tone: "profit", dashed: true },
  ];
}

// ── The grading rules, as small pure functions (tested on their own) ────────

/** Reward to the target ÷ risk to the stop. */
export function rewardRisk(entry: number, stop: number, target: number): number {
  const risk = Math.abs(entry - stop);
  return risk > 0 ? Math.abs(target - entry) / risk : 0;
}

/** Don't chase: price ran more than 0.3 ATR past the entry → skip. Null near the line, or when price ran well against the trade. */
export function chaseVerdict(runAtr: number): "enter" | "skip" | null {
  if (runAtr < -CHASE_ATR || Math.abs(runAtr - CHASE_ATR) < 0.05) return null;
  return runAtr > CHASE_ATR ? "skip" : "enter";
}

/** The entry window: under 2 hours since the signal → still valid; 2 hours or more → stale. Null within 10 minutes of 2:00. */
export function entryWindowVerdict(minutes: number): "valid" | "stale" | null {
  const limit = ENTRY_WINDOW_HOURS * 60;
  if (Math.abs(minutes - limit) < 10) return null;
  return minutes < limit ? "valid" : "stale";
}

/** Coins to trade: the risk in $ (halved against BTC) ÷ the loss per coin at the stop. */
export function btcSize(riskUsd: number, entry: number, stop: number, againstBtc: boolean): number {
  return (riskUsd * (againstBtc ? AGAINST_BTC_RISK : 1)) / Math.abs(entry - stop);
}

/** The time stop: past 24 hours with no stop or target → close; before → hold. Null at exactly 24. */
export function timeStopVerdict(hoursOpen: number): "close" | "hold" | null {
  if (hoursOpen === TIME_STOP_HOURS) return null;
  return hoursOpen > TIME_STOP_HOURS ? "close" : "hold";
}

/** Half booked at target 1, half closed at `secondR` (0 at the entry, or target 2). */
export function splitR(tp1R: number, secondR: number): number {
  return 0.5 * tp1R + 0.5 * secondR;
}

/** Room to a level ahead of the entry, in R (backend keyLevels.levelInTheWay). */
export function roomR(side: Side, entry: number, stop: number, level: number): number {
  const risk = Math.abs(entry - stop);
  return risk > 0 ? ((side === "LONG" ? level - entry : entry - level) / risk) : 0;
}

/** Under 0.5R of room → blocked; null within 0.05R of the line (or a level that isn't ahead). */
export function roomVerdict(room: number): "blocked" | "ok" | null {
  if (!(room > 0) || Math.abs(room - MIN_ROOM_R) < 0.05) return null;
  return room < MIN_ROOM_R ? "blocked" : "ok";
}

// ── 11. Which plan pays enough? ─────────────────────────────────────────────

export const rrGate: GameDef = {
  id: "rr-gate",
  level: "intermediate",
  title: "Which plan pays enough?",
  skill: "Check reward ÷ risk: the engine needs target 1 to pay at least 1.5× the risk.",
  make(_data, rng) {
    const coin = rng.pick(COINS);
    const dec = tickDecimals(coin.tick);
    const right = rng.pick([1.5, 1.6, 1.8, 2, 2.2, 2.5, 3]);
    const wrongs = rng.shuffle([0.6, 0.7, 0.8, 0.9, 1, 1.1, 1.2, 1.3, 1.4]).slice(0, 3);
    // At least one buy and one sell among the four.
    const sides = rng.shuffle<Side>(["LONG", "SHORT", rng.chance(0.5) ? "LONG" : "SHORT", rng.chance(0.5) ? "LONG" : "SHORT"]);
    const plans = rng.shuffle([right, ...wrongs]).map((want, i) => {
      const p = makePlan(coin, rng, [0.6, 2.5], sides[i]);
      const rewardTicks = Math.round(want * p.riskTicks);
      const reward = roundTo(rewardTicks * coin.tick, dec);
      return { ...p, id: ["a", "b", "c", "d"][i]!, ratio: rewardTicks / p.riskTicks, reward, target: snap(p.entry + p.dir * reward, coin.tick) };
    });
    const best = plans.find(p => p.ratio >= MIN_RR);
    if (!best || plans.filter(p => p.ratio >= MIN_RR).length !== 1 || plans.some(p => !(p.target > 0))) return null;
    const label = (p: (typeof plans)[number]) => `${BuyWord(p.side)} · Entry ${price(p.entry)} · Stop ${price(p.stop)} · Target ${price(p.target)}`;
    const options: Option[] = plans.map(p => ({
      id: p.id,
      label: label(p),
      why: p === best ? undefined : `Reward ${price(p.reward)} ÷ risk ${price(p.risk)} = ${p.ratio.toFixed(1)}×: under the ${MIN_RR}× minimum.`,
    }));
    if (new Set(options.map(o => o.label)).size !== options.length) return null;
    return {
      kind: "choice",
      prompt: `The engine only takes a plan whose target 1 pays at least ${MIN_RR}× its risk. Which of these ${coin.name} plans pays enough?`,
      facts: [{ label: "Rule", value: `Reward ÷ risk ≥ ${MIN_RR}` }],
      options,
      answer: best.id,
      explain: `Reward ÷ risk = the target's distance from the entry ÷ the stop's distance. The ${buyWord(best.side)} at ${price(best.entry)}: ${price(best.reward)} ÷ ${price(best.risk)} = ${best.ratio.toFixed(1)}×, at least ${MIN_RR}×. The other three pay under ${MIN_RR}×, so the engine skips them.`,
    };
  },
};

// ── 12. Fees vs your risk ───────────────────────────────────────────────────

export const costCheck: GameDef = {
  id: "cost-check",
  level: "intermediate",
  title: "Fees vs your risk",
  skill: "Turn fees, slippage and funding into R, and see why a stop can't be too tight.",
  make(_data, rng) {
    const coin = rng.pick(COINS);
    const side: Side = rng.chance(0.5) ? "LONG" : "SHORT";
    const dir = side === "LONG" ? 1 : -1;
    const entry = snap(coin.px * rng.float(0.9, 1.1), coin.tick);
    const tight = rng.chance(0.5);
    const stopPct = tight ? rng.pick([0.3, 0.35, 0.4, 0.45, 0.5, 0.55]) : rng.pick([0.75, 0.8, 0.9, 1, 1.2, 1.5, 1.8, 2]);
    const stop = entry * (1 - (dir * stopPct) / 100);
    const hours = 6;
    const answer = costR(entry, stop, hours);
    const tripPct = roundTripCostPct(hours);
    const fails = answer > MAX_COST_R;
    return {
      kind: "number",
      prompt: `You ${buyWord(side)} ${coin.name} with the stop ${pctText(stopPct)} away. How much of your risk do fees, slippage and funding eat on the round trip, in R (one unit of the risk you planned)?`,
      facts: [
        { label: "Entry", value: price(entry) },
        { label: "Stop distance", value: pctText(stopPct) },
        { label: "Fee", value: `${pctText(FEE_PCT_PER_SIDE)} per side (in and out)` },
        { label: "Slippage", value: `${pctText(SLIPPAGE_PCT_PER_SIDE, 3)} per side` },
        { label: "Funding", value: `${pctText(FUNDING_PCT_PER_8H)} per 8 hours` },
        { label: "Time held", value: `${hours} hours` },
      ],
      answer,
      tolerance: 0.02,
      unit: "R",
      decimals: 2,
      explain: `Costs: 2 × (${pctText(FEE_PCT_PER_SIDE)} + ${pctText(SLIPPAGE_PCT_PER_SIDE, 3)}) + ${hours}/8 × ${pctText(FUNDING_PCT_PER_8H)} = ${pctText(tripPct, 4)} of the position; ÷ the ${pctText(stopPct)} stop = ${answer.toFixed(2)}R. ${fails ? `That's over ${MAX_COST_R}R, so the engine rejects the trade: a stop this tight pays too much of its risk in fees.` : `That's under the ${MAX_COST_R}R limit, so the cost gate passes (stops tighter than about ${pctText(tripPct / MAX_COST_R)} fail it).`}`,
    };
  },
};

// ── 13. Chase or skip? ──────────────────────────────────────────────────────

export const chaseOrSkip: GameDef = {
  id: "chase-or-skip",
  level: "intermediate",
  title: "Chase or skip?",
  skill: "Skip an entry once price has run more than 0.3 ATR past it: don't chase.",
  needs: { tf: "1h", history: 80, ahead: 12 },
  make(data) {
    if (!data || data.candles.length < 60) return null;
    const c = data.candles;
    // The signal fired at the close one candle before now: still inside its 2-hour entry window.
    const s = c.length - 2;
    const upto = c.slice(0, s + 1);
    const e20 = emaLast(upto, 20), e50 = emaLast(upto, 50), a = atr(upto);
    if (e20 === null || e50 === null || a === null || !(a > 0) || e20 === e50) return null;
    const side: Side = e20 > e50 ? "LONG" : "SHORT";
    const dir = side === "LONG" ? 1 : -1;
    const signal = c[s]!;
    const entry = signal.close;
    const now = c[c.length - 1]!.close;
    const run = (dir * (now - entry)) / a;
    const verdict = chaseVerdict(run);
    if (!verdict) return null;
    const line = entry + dir * CHASE_ATR * a;
    const skip = verdict === "skip";
    const moved = `${price(Math.abs(now - entry))} ${run >= 0 ? "in the trade's favour" : "against the trade"}`;
    const ahead = data.ahead;
    let outcome: string | undefined;
    if (ahead.length > 0) {
      const back = run > 0 && ahead.some(k => (side === "LONG" ? k.low <= entry : k.high >= entry));
      outcome = `Over the next ${ahead.length} hours price went from ${price(now)} to ${price(ahead[ahead.length - 1]!.close)}${run > 0 ? (back ? `, and it did trade back at the entry (${price(entry)})` : `, and it never came back to the entry (${price(entry)})`) : ""}.`;
    }
    return {
      kind: "choice",
      prompt: `The engine signalled a ${BUYWORD(side)} at the close of candle S, one hour ago. Price has moved since then: do you still ${buyWord(side)} now?`,
      facts: [
        { label: "Signal", value: `${BUYWORD(side)} ${data.coin}` },
        { label: "Entry", value: price(entry) },
        { label: "ATR (average candle size)", value: price(a) },
        { label: "Price now", value: price(now) },
      ],
      chart: chartOf(data, {
        focus: 40,
        emas: [20, 50],
        lines: [{ price: entry, label: "Entry", tone: "brand" }],
        marks: [{ time: signal.time, label: "S", tone: "brand", position: side === "LONG" ? "below" : "above" }],
        answerLines: [{ price: line, label: "Don't chase", tone: "warning", dashed: true }],
      }),
      options: [
        { id: "enter", label: "Enter: still close to the entry", why: skip ? `Price already ran ${atrText(run)} past the entry, beyond the ${CHASE_ATR} ATR line. Chasing means a worse price with the same stop.` : undefined },
        { id: "skip", label: "Skip: it ran too far, don't chase", why: skip ? undefined : run < 0 ? `Price is ${atrText(-run)} against the trade, not past the entry at all: nothing to chase.` : `Price is only ${atrText(run)} past the entry, inside the ${CHASE_ATR} ATR line: the plan still works at this price.` },
      ],
      answer: verdict,
      explain: `Price moved ${moved}: ${price(Math.abs(now - entry))} ÷ ATR ${price(a)} = ${atrText(Math.abs(run))}. The don't-chase line is ${CHASE_ATR} ATR past the entry, at ${price(line)}. ${skip ? "Price is past it, so the reward no longer pays for the risk: skip it and wait for the next signal." : "Price hasn't crossed it, so the entry is still good."}`,
      outcome,
    };
  },
};

// ── 14. Still on time? ──────────────────────────────────────────────────────

const DAY0 = 1_767_225_600; // 1 Jan 2026 00:00 UTC

export const entryWindow: GameDef = {
  id: "entry-window",
  level: "intermediate",
  title: "Still on time?",
  skill: "A signal's entry is good for 2 hours after it fires; after that it's stale.",
  make(_data, rng) {
    const coin = rng.pick(COINS);
    const p = makePlan(coin, rng, [0.8, 2]);
    const fired = DAY0 + rng.int(0, 280) * 86400 + rng.int(0, 19) * 3600;
    const stale = rng.chance(0.5);
    const minutes = stale ? rng.int(26, 47) * 5 : rng.int(1, 22) * 5;
    const verdict = entryWindowVerdict(minutes);
    if (!verdict) return null;
    const nowSec = fired + minutes * 60;
    // Price is a little away from the entry in the trade's favour: the entry order hasn't filled, and it hasn't run off.
    const nowPrice = snap(p.entry * (1 + (p.dir * rng.float(0.04, 0.15)) / 100), coin.tick);
    if (nowPrice === p.entry) return null;
    const valid = verdict === "valid";
    return {
      kind: "choice",
      prompt: `The engine signalled a ${buyWord(p.side)} on ${coin.name}. Price hasn't reached the entry yet, and it's still close to it. Is the signal still on time?`,
      facts: [
        { label: "Signal", value: `${BUYWORD(p.side)} ${coin.name} at ${price(p.entry)}` },
        { label: "Signal fired", value: `${clockText(fired)} UTC (a 1h close)` },
        { label: "Now", value: `${clockText(nowSec)} UTC` },
        { label: "Price now", value: `${price(nowPrice)} (entry not filled yet)` },
      ],
      options: [
        { id: "valid", label: "Still valid: wait for the entry", why: valid ? undefined : `It's been ${durationText(minutes)}: past the ${ENTRY_WINDOW_HOURS}-hour window. The setup it was based on is old news.` },
        { id: "stale", label: "Stale: skip it", why: valid ? `Only ${durationText(minutes)} since the signal: it's inside the ${ENTRY_WINDOW_HOURS}-hour window, so it still counts.` : undefined },
      ],
      answer: verdict,
      explain: `${clockText(fired)} to ${clockText(nowSec)} is ${durationText(minutes)}. A signal's entry is good for ${ENTRY_WINDOW_HOURS} hours (two 1h candles). ${valid ? "Inside the window: keep waiting for the entry price." : "Past the window: skip it and wait for a fresh signal."}`,
    };
  },
};

// ── 15. Ready or not? ───────────────────────────────────────────────────────

const QUIET_SEC = 6 * 3600;

export const readyOrNot: GameDef = {
  id: "ready-or-not",
  level: "intermediate",
  title: "Ready or not?",
  skill: "Tell a close where the engine fires a signal from the many where it waits.",
  needs: { tf: "1h", history: SIGNAL_HISTORY, ahead: SIGNAL_AHEAD },
  signals: true,
  make(data, rng) {
    const signals = engineSignals(data?.signals);
    if (!data || signals.length === 0) return null;
    const c = data.candles;
    if (c.length <= SIGNAL_HISTORY) return null;
    let sig: ChartSignal | null = null;
    let nowSec: number;
    if (rng.chance(0.5)) {
      sig = rng.pick(signals);
      nowSec = sig.signalTime;
    } else {
      // A 1h close in the signal window with no signal within 6 hours of it.
      const quiet: number[] = [];
      for (let i = SIGNAL_HISTORY; i < c.length; i++) {
        const t = c[i]!.time + 3600;
        if (signals.every(s => Math.abs(s.signalTime - t) >= QUIET_SEC)) quiet.push(t);
      }
      if (quiet.length === 0) return null;
      nowSec = rng.pick(quiet);
    }
    const { shown, ahead } = cutAt(c, nowSec, 3600, SIGNAL_HISTORY, SIGNAL_AHEAD);
    if (shown.length < 60) return null;
    const last = shown[shown.length - 1]!;
    const answer = sig ? BUYWORD(sig.action) : "none";
    const noSignalWhy = `No signal fired within 6 hours of this close: nothing lined up.`;
    const options: Option[] = [
      { id: "BUY", label: "BUY", why: answer === "BUY" ? undefined : answer === "SELL" ? `The engine sold here, not bought: ${sig ? setupText(sig) : ""}.` : noSignalWhy },
      { id: "SELL", label: "SELL", why: answer === "SELL" ? undefined : answer === "BUY" ? `The engine bought here, not sold: ${sig ? setupText(sig) : ""}.` : noSignalWhy },
      { id: "none", label: "No trade", why: answer === "none" ? undefined : `The engine did fire here: ${sig ? setupText(sig) : ""}.` },
    ];
    let outcome: string | undefined;
    if (sig) outcome = signalOutcome(sig) || undefined;
    else if (ahead.length > 0) outcome = `Over the next ${ahead.length} hours price went from ${price(last.close)} to ${price(ahead[ahead.length - 1]!.close)}.`;
    return {
      kind: "choice",
      prompt: `It's the close of the candle marked “?” on ${data.coin}. Did the engine fire a signal here: BUY, SELL, or no trade?`,
      chart: {
        tf: "1h",
        candles: shown,
        ahead,
        focus: 60,
        emas: [20, 50],
        marks: [{ time: last.time, label: "?", tone: "brand", position: "above" }],
        answerLines: sig ? planLines(sig) : undefined,
      },
      options,
      answer,
      explain: sig
        ? `The engine fired a ${BUYWORD(sig.action)} here: ${setupText(sig)}.${thesisText(sig)}`
        : "No signal fired within 6 hours of this close. Most 1h closes have no setup: the engine waits for a trend pullback, range fade or breakout retest plus a trigger candle. Waiting is the job.",
      outcome,
    };
  },
};

// ── 16. The engine's stop ───────────────────────────────────────────────────

function stopReason(s: ChartSignal): string {
  const long = s.action === "LONG";
  switch (s.archetype) {
    case "trend_pullback":
      return `For a trend pullback it goes past the pullback's ${long ? "lowest low" : "highest high"} (of the last ${STOP_LOOKBACK} candles), plus a buffer of ${STOP_BUFFER_ATR} ATR (ATR = the average candle size).`;
    case "breakout_retest":
      return `For a breakout retest it goes past the level that broke, plus 0.3 ATR (ATR = the average candle size): back through the level means the breakout failed.`;
    case "range_fade":
      return `For a range fade it goes past the range's ${long ? "bottom" : "top"} edge, plus 0.5 ATR (ATR = the average candle size): a break out of the range means the fade was wrong.`;
    default:
      return "It goes behind the swing the setup is built on, plus a small buffer.";
  }
}

export const signalStop: GameDef = {
  id: "signal-stop",
  level: "intermediate",
  title: "The engine's stop",
  skill: "Find where the engine puts a real signal's stop: behind the swing, plus a buffer.",
  needs: { tf: "1h", history: SIGNAL_HISTORY, ahead: SIGNAL_AHEAD },
  signals: true,
  make(data, rng) {
    const signals = engineSignals(data?.signals);
    if (!data || signals.length === 0) return null;
    const s = rng.pick(signals);
    const long = s.action === "LONG";
    const risk = Math.abs(s.entry - s.stopLoss);
    if (!(risk > 0) || (long ? s.stopLoss >= s.entry : s.stopLoss <= s.entry)) return null;
    const { shown, ahead } = cutAt(data.candles, s.signalTime, 3600, SIGNAL_HISTORY, SIGNAL_AHEAD);
    if (shown.length < 60) return null;
    const lo = Math.min(...shown.map(k => k.low));
    const hi = Math.max(...shown.map(k => k.high));
    const pad = (hi - lo) * 0.5;
    if (s.stopLoss < lo - pad || s.stopLoss > hi + pad) return null;
    return {
      kind: "price",
      prompt: `The engine sent a ${BUYWORD(s.action)} on ${data.coin} at this close (entry line). Click where it put the stop.`,
      chart: {
        tf: "1h",
        candles: shown,
        ahead,
        focus: 60,
        emas: [20, 50],
        lines: [{ price: s.entry, label: "Entry", tone: "brand" }],
        answerLines: [
          { price: s.stopLoss, label: "Stop", tone: "loss" },
          { price: s.tp1, label: "Target 1", tone: "profit", dashed: true },
        ],
      },
      answer: s.stopLoss,
      accept: { from: s.stopLoss - 0.25 * risk, to: s.stopLoss + 0.25 * risk },
      explain: `${stopReason(s)} Here the stop is ${price(s.stopLoss)}, ${price(risk)} (${pctText((risk / s.entry) * 100)}) ${long ? "below" : "above"} the entry; anywhere within a quarter of that distance counts.`,
      outcome: signalOutcome(s) || undefined,
    };
  },
};

// ── 17. Half-risk size ──────────────────────────────────────────────────────

export const againstBtcSize: GameDef = {
  id: "against-btc-size",
  level: "intermediate",
  title: "Half-risk size",
  skill: "Size a trade at half risk when it goes against BTC's strong trend.",
  make(_data, rng) {
    const coin = rng.pick(COINS.filter(k => k.name !== "BTC"));
    const p = makePlan(coin, rng, [0.8, 3]);
    const riskUsd = rng.pick([5, 10, 15, 20, 25, 50]);
    const against = rng.chance(0.6);
    const answer = btcSize(riskUsd, p.entry, p.stop, against);
    if (!Number.isFinite(answer) || answer <= 0) return null;
    const decimals = sizeDecimals(answer);
    const used = riskUsd * (against ? AGAINST_BTC_RISK : 1);
    return {
      kind: "number",
      prompt: `You ${buyWord(p.side)} ${coin.name}. A signal against BTC's strong trend trades at half the normal risk. How many ${coin.name} do you ${buyWord(p.side)}?`,
      facts: [
        { label: "Normal risk", value: usd(riskUsd) },
        { label: "Entry", value: price(p.entry) },
        { label: "Stop", value: price(p.stop) },
        { label: "Against BTC?", value: against ? `Yes: BTC is trending hard the other way` : `No: this ${buyWord(p.side)} goes with BTC's trend` },
      ],
      answer,
      tolerance: answer * 0.02,
      unit: "coins",
      decimals,
      explain: `${against ? `Against BTC, so half the risk: ${usd(riskUsd)} × ${AGAINST_BTC_RISK} = ${usd(used)}.` : `With BTC, so the full ${usd(riskUsd)} risk.`} ${usd(used)} ÷ ${price(p.risk)} lost per coin at the stop = ${answer.toFixed(decimals)} ${coin.name}.`,
    };
  },
};

// ── 18. Time's up? ──────────────────────────────────────────────────────────

export const timeStop: GameDef = {
  id: "time-stop",
  level: "intermediate",
  title: "Time's up?",
  skill: "Close a 1h pullback trade that made no progress in 24 hours; never widen the stop.",
  make(_data, rng) {
    const coin = rng.pick(COINS);
    const p = makePlan(coin, rng, [0.8, 2.5]);
    const tp1 = priceAtR(p, TP1_FALLBACK_R, coin);
    const over = rng.chance(0.5);
    const hours = over ? rng.int(25, 40) : rng.int(6, 23);
    const verdict = timeStopVerdict(hours);
    if (!verdict) return null;
    const nowR = rng.pick([-0.3, -0.2, -0.1, 0.1, 0.2, 0.3]);
    const now = priceAtR(p, nowR, coin);
    const close = verdict === "close";
    return {
      kind: "choice",
      prompt: `Your 1h trend-pullback ${buyWord(p.side)} on ${coin.name} has hit neither the stop nor target 1, and price sits near the entry. What do you do?`,
      facts: [
        { label: "Trade", value: `${BuyWord(p.side)} ${coin.name} at ${price(p.entry)}` },
        { label: "Stop · Target 1", value: `${price(p.stop)} · ${price(tp1)}` },
        { label: "Price now", value: `${price(now)} (${rText(nowR)})` },
        { label: "Open for", value: `${hours} hours` },
      ],
      options: [
        { id: "close", label: `Close it: no progress in ${TIME_STOP_HOURS} hours`, why: close ? undefined : `Only ${hours} hours in: the plan gives a 1h pullback ${TIME_STOP_HOURS} hours to work, and nothing is broken yet.` },
        { id: "hold", label: "Hold: the plan still has time", why: close ? `${hours} hours is past the ${TIME_STOP_HOURS}-hour limit. The idea had its chance; close it and free your risk for the next one.` : undefined },
        { id: "widen", label: "Move the stop further away to give it room", why: "Widening the stop turns a planned 1R loss into a bigger one. The engine never moves a stop away from the entry." },
      ],
      answer: verdict,
      explain: `A 1h pullback that reaches neither the stop nor the target in ${TIME_STOP_HOURS} hours is closed: the move it expected didn't come. This one is ${hours} hours old, so ${close ? "close it at the market price" : "hold and let the plan play out"}.`,
    };
  },
};

// ── 19. Target 1, then breakeven ────────────────────────────────────────────

export const tp1ThenBreakeven: GameDef = {
  id: "tp1-then-breakeven",
  level: "intermediate",
  title: "Target 1, then breakeven",
  skill: "Work out a whole trade in R when half closes at target 1 and the stop moves to the entry.",
  make(_data, rng) {
    const coin = rng.pick(COINS);
    const p = makePlan(coin, rng, [0.8, 2.5]);
    // Target 1 at the 1.5R fallback, or at a structural level a bit further (the engine needs at least 1.5R).
    const want = rng.chance(0.5) ? TP1_FALLBACK_R : rng.pick([1.6, 1.7, 1.8, 1.9, 2]);
    const tp1 = priceAtR(p, want, coin);
    const tp2 = priceAtR(p, TP2_R, coin);
    if (!(tp1 > 0) || !(tp2 > 0)) return null;
    const r1 = Math.abs(tp1 - p.entry) / p.risk;
    const r2 = Math.abs(tp2 - p.entry) / p.risk;
    const toTp2 = rng.chance(0.5);
    const second = toTp2 ? r2 : 0;
    const answer = splitR(r1, second);
    return {
      kind: "number",
      prompt: `The engine books half at target 1 and moves the stop to the entry. What did this whole ${buyWord(p.side)} make, in R (one unit of the risk you planned)?`,
      facts: [
        { label: "Trade", value: `${BuyWord(p.side)} ${coin.name} at ${price(p.entry)}` },
        { label: "Stop", value: price(p.stop) },
        { label: "Target 1 · Target 2", value: `${price(tp1)} · ${price(tp2)}` },
        { label: "What happened", value: toTp2 ? "Target 1 hit, then target 2 hit" : "Target 1 hit, then price came back to the entry" },
      ],
      answer,
      tolerance: 0.02,
      unit: "R",
      decimals: 2,
      explain: `Risk ${price(p.risk)} per coin. Target 1: ${price(Math.abs(tp1 - p.entry))} ÷ ${price(p.risk)} = ${r1.toFixed(2)}R on half; the other half ${toTp2 ? `reached target 2: ${price(Math.abs(tp2 - p.entry))} ÷ ${price(p.risk)} = ${r2.toFixed(2)}R` : "closed at the entry: 0R"}. 0.5 × ${r1.toFixed(2)} + 0.5 × ${second.toFixed(2)} = ${answer.toFixed(2)}R.`,
    };
  },
};

// ── 20. Room to the target? ─────────────────────────────────────────────────

export const levelInTheWay: GameDef = {
  id: "level-in-the-way",
  level: "intermediate",
  title: "Room to the target?",
  skill: "Block a trade when a level sits less than 0.5R past the entry.",
  make(_data, rng) {
    const coin = rng.pick(COINS);
    const p = makePlan(coin, rng, [0.8, 2.5]);
    const tight = rng.chance(0.5);
    const want = tight ? rng.float(0.1, 0.42) : rng.float(0.6, 1.4);
    const level = priceAtR(p, want, coin);
    const tp1 = priceAtR(p, TP1_FALLBACK_R, coin);
    const room = roomR(p.side, p.entry, p.stop, level);
    const verdict = roomVerdict(room);
    if (!verdict || !(tp1 > 0)) return null;
    const blocked = verdict === "blocked";
    const kind = p.side === "LONG" ? "resistance" : "support";
    const dist = Math.abs(level - p.entry);
    return {
      kind: "choice",
      prompt: `You plan a ${buyWord(p.side)} on ${coin.name}. There's ${kind} between the entry and target 1. Is there enough room to take the trade?`,
      facts: [
        { label: "Trade", value: `${BuyWord(p.side)} ${coin.name} at ${price(p.entry)}` },
        { label: "Stop", value: price(p.stop) },
        { label: p.side === "LONG" ? "Resistance ahead" : "Support ahead", value: price(level) },
        { label: "Target 1", value: price(tp1) },
      ],
      options: [
        { id: "blocked", label: "Blocked: the level is too close", why: blocked ? undefined : `The ${kind} is ${room.toFixed(2)}R away: at least ${MIN_ROOM_R}R, so it isn't in the way.` },
        { id: "ok", label: "OK: enough room", why: blocked ? `Only ${room.toFixed(2)}R of room: under ${MIN_ROOM_R}R, price will likely stall at the ${kind} before the trade pays.` : undefined },
      ],
      answer: verdict,
      explain: `Room = distance to the ${kind} ÷ risk = ${price(dist)} ÷ ${price(p.risk)} = ${room.toFixed(2)}R. ${blocked ? `Under ${MIN_ROOM_R}R, the engine blocks the trade: price tends to stall at the level before it pays.` : `That's at least ${MIN_ROOM_R}R, so the level isn't in the way.`}`,
    };
  },
};

export const GAMES: GameDef[] = [
  rrGate,
  costCheck,
  chaseOrSkip,
  entryWindow,
  readyOrNot,
  signalStop,
  againstBtcSize,
  timeStop,
  tp1ThenBreakeven,
  levelInTheWay,
];
